import { isEqualWith } from 'lodash'

import { Services, joinURLPath } from '@sigma-frontend-libs/commons'

import { RequestCollector, globalErrorHandler, request } from '@/utils'

export const fullCountryMNC = '00'
export const fullCountryValue = 'all'

const requestCollector = new RequestCollector({
  request: async data => {
    const response = await request.create(joinURLPath(Services.dictionaries, 'getOperatorsData'), { body: { data } })

    return response || []
  },

  dataFilter: (data, bucket) => {
    const result = []
    const customizer = (d, b) => {
      if (!Array.isArray(d)) {
        d = [ d ]
      }
      if (!Array.isArray(b)) {
        b = [ b ]
      }

      return d.every(item => {
        return ~b.indexOf(item)
      })
    }

    for (const { mcc, mnc } of data) {
      const bucketIndexByMCC = bucket.findIndex(item => {
        return item.mcc === mcc
      })
      const bucketElement = bucket[bucketIndexByMCC]
      if (~bucketIndexByMCC) {
        if (bucketElement.mnc === fullCountryValue || isEqualWith(mnc, bucketElement.mnc, customizer)) {
          continue
        }
      }

      result.push({
        mcc,
        mnc
      })
    }

    return result
  }
})

export function isFullCountryValue(value) {
  return value === fullCountryValue || value === fullCountryMNC
}

function findOperatorGroup(groups, mcc, mnc) {
  return groups.find(group => {
    return group.mcc === mcc && group.mnc === mnc
  })
}

function findFullCountryGroup(groups, mcc, mnc) {
  const operatorGroup = findOperatorGroup(groups, mcc, mnc)
  if (operatorGroup) {
    return operatorGroup
  }

  const legacyOperatorGroup = findOperatorGroup(groups, mcc, fullCountryMNC)
  if (legacyOperatorGroup) {
    return legacyOperatorGroup
  }

  return groups.find(group => {
    return group.mcc === mcc
  })
}

function findValueGroup(groups, mcc, mnc, fullCountry) {
  if (fullCountry) {
    return findFullCountryGroup(groups, mcc, mnc)
  }

  return findOperatorGroup(groups, mcc, mnc)
}

export const mccObjectToArray = {
  props: {
    value: {
      type: Object,
      default: () => {
        return {}
      }
    },
    count: {
      type: Number,
      default: 3
    }
  },

  data() {
    return {
      loading: false,

      search: undefined,

      groups: []
    }
  },

  computed: {
    values() {
      const result = []

      if (this.value) {
        for (const mcc in this.value) {
          const mnc = this.value[mcc]
          switch (true) {
            case Array.isArray(mnc): {
              for (const item of mnc) {
                result.push({
                  mcc,
                  mnc: item
                })
              }
              break
            }
            case typeof mnc === 'string': {
              result.push({
                mcc,
                mnc,
                fullCountry: true
              })
              break
            }
          }
        }
      }

      return result.reduce((result, { mcc, mnc, fullCountry }) => {
        const group = findValueGroup(this.groups, mcc, mnc, fullCountry) || {}
        const { country } = group
        let operator = group.operator
        if (fullCountry) {
          operator = undefined
        }

        let check = true

        if (this.search) {
          const search = `${this.search}`.toLowerCase()
          const values = [ mcc, mnc, country, operator ].map(value => {
            return `${value}`.toLowerCase()
          })

          check = !!~values.findIndex(element => {
            return element.includes(search)
          })
        }

        if (check) {
          result.push({
            mcc,
            mnc,
            country,
            operator,
            fullCountry
          })
        }

        return result
      }, []).sort((a, b) => {
        return a.mcc - b.mcc
      })
    }
  },

  watch: {
    value: {
      handler() {
        if (!this.loading) {
          this.find()
        }
      },
      deep: true
    }
  },

  mounted() {
    this.find()
  },

  methods: {
    async find() {
      try {
        this.loading = true

        if (this.value) {
          const parsedValue = Object.keys(this.value).map(mcc => {
            let mnc = this.value[mcc]
            if (Array.isArray(this.value[mcc])) {
              mnc = this.value[mcc].sort()
            }

            return {
              mcc,
              mnc
            }
          })
          const response = await requestCollector.getData(parsedValue)
          if (response) {
            this.groups = response
          }
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading = false
      }
    }
  }
}
